import React from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Star, MapPin, Home, Tag, Calendar } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { PLACEHOLDER_IMAGE } from "@/lib/data";
import { thumbOf } from "@/lib/media";

export function BusinessCard({ biz, favorited, onToggleFav, index = 0 }) {
  const navigate = useNavigate();

  return (
    <div
      data-testid={`business-card-${biz.id}`}
      onClick={() => navigate(`/business/${biz.id}`)}
      className="group cursor-pointer bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.10)] hover:-translate-y-1 transition-all duration-300 animate-fade-up"
      style={{ animationDelay: `${index * 60}ms`, opacity: 0 }}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={thumbOf(biz, PLACEHOLDER_IMAGE)} alt={biz.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <div className="absolute top-3 left-3 flex gap-2">
          {biz.home_based && (
            <Badge className="bg-white/90 text-slate-900 rounded-full backdrop-blur-sm">
              <Home className="w-3 h-3 mr-1" /> Home-based
            </Badge>
          )}
          {biz.hidden_gem && (
            <Badge className="bg-teal-500 text-white rounded-full">Hidden Gem</Badge>
          )}
        </div>
        {onToggleFav && (
          <button
            data-testid={`fav-btn-${biz.id}`}
            onClick={(e) => { e.stopPropagation(); onToggleFav(biz.id); }}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center hover:scale-110 transition-transform"
          >
            <Heart className={`w-5 h-5 ${favorited ? "fill-orange-500 text-orange-500" : "text-slate-700"}`} strokeWidth={1.5} />
          </button>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-display text-lg font-semibold tracking-tight leading-tight">{biz.name}</h3>
          {biz.rating != null ? (
            <div className="flex items-center gap-1 text-sm shrink-0">
              <Star className="w-4 h-4 fill-orange-400 text-orange-400" />
              <span className="font-medium">{biz.rating.toFixed(1)}</span>
            </div>
          ) : (
            <span className="text-xs font-medium bg-teal-50 text-teal-600 px-2 py-0.5 rounded-full shrink-0">New</span>
          )}
        </div>
        <p className="text-sm text-slate-500 mt-1 flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5" /> {biz.address}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {biz.categories?.slice(0, 2).map((c) => (
            <span key={c} className="text-xs bg-[#F3F0EA] text-slate-700 px-2.5 py-1 rounded-full">{c}</span>
          ))}
        </div>
        {(biz.deals_count > 0 || biz.events_count > 0) && (
          <div className="flex gap-3 mt-3 text-xs text-slate-500">
            {biz.deals_count > 0 && <span className="flex items-center gap-1 text-orange-600"><Tag className="w-3.5 h-3.5" /> {biz.deals_count} deal{biz.deals_count > 1 ? "s" : ""}</span>}
            {biz.events_count > 0 && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {biz.events_count} event{biz.events_count > 1 ? "s" : ""}</span>}
          </div>
        )}
      </div>
    </div>
  );
}
